import React, { useState, useRef, useCallback, useMemo, useEffect, memo } from 'react';
import Editor from '@monaco-editor/react';
import {
  CURSOR_DARK_THEME,
  LANG_STYLE,
  detectLang,
  registerEditorAssets,
  clearEditorMarkers,
  EDITOR_BASE_OPTIONS,
  EDITOR_THEMES,
  EDITOR_FONT_FAMILIES,
  EDITOR_SETTINGS_DEFAULTS,
  loadEditorSettings,
  saveEditorSettings,
} from './codeEditorShared';

/* ── ThemeCard: dışarıda tanımlı → iç içe re-mount yok ── */
const ThemeCard = memo(({ theme, active, onSelect }) => {
  const [bg, fg, kw, str] = theme.preview;
  return (
    <button
      onClick={() => onSelect(theme.id)}
      title={theme.name}
      style={{
        width: '100%',
        padding: '6px 8px',
        background: active ? '#1f6feb20' : 'transparent',
        border: `1px solid ${active ? '#388bfd' : '#30363d'}`,
        borderRadius: 5,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        transition: 'all 0.12s',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = '#484f58'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = '#30363d'; }}
    >
      <div style={{
        width: 32, height: 22, borderRadius: 3, overflow: 'hidden', flexShrink: 0,
        background: bg, border: '1px solid #ffffff18',
        display: 'flex', flexDirection: 'column', gap: 2, padding: '3px 4px',
      }}>
        <div style={{ height: 3, borderRadius: 1, background: kw, width: '60%' }} />
        <div style={{ height: 3, borderRadius: 1, background: str, width: '80%' }} />
        <div style={{ height: 3, borderRadius: 1, background: fg + '88', width: '50%' }} />
      </div>
      <span style={{
        color: active ? '#e6edf3' : '#8b949e',
        fontSize: 11,
        fontWeight: active ? 600 : 400,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        flex: 1, textAlign: 'left',
      }}>{theme.name}</span>
      {active && <span style={{ color: '#388bfd', fontSize: 10 }}>✓</span>}
    </button>
  );
});

/* ── Settings Panel ── */
const DARK_THEMES  = EDITOR_THEMES.filter((t) => t.dark);
const LIGHT_THEMES = EDITOR_THEMES.filter((t) => !t.dark);

const SELECT_STYLE = {
  background: '#161b22', border: '1px solid #30363d', color: '#e6edf3',
  borderRadius: 4, fontSize: 11, padding: '3px 6px', outline: 'none', cursor: 'pointer',
};

const SettingsPanel = memo(({ settings, onChange, onClose }) => {
  const update = useCallback((key, val) => {
    const next = { ...settings, [key]: val };
    onChange(next);
    saveEditorSettings(next);
  }, [settings, onChange]);

  const handleThemeSelect = useCallback((id) => update('theme', id), [update]);

  const sectionTitle = (label) => (
    <div style={{ color: '#8b949e', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6, marginTop: 14 }}>
      {label}
    </div>
  );

  const row = (label, children) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ color: '#8b949e', fontSize: 11 }}>{label}</span>
      {children}
    </div>
  );

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      background: '#0d1117', display: 'flex', flexDirection: 'column',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        background: '#010409', borderBottom: '1px solid #21262d',
        padding: '0 12px', height: 34,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span style={{ color: '#e6edf3', fontWeight: 600, fontSize: 12 }}>⚙ Editor Ayarları</span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: '#484f58', fontSize: 18,
          cursor: 'pointer', lineHeight: 1, padding: '2px 4px',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#e6edf3'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
        >×</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 14px 14px', minHeight: 0 }}>
        {sectionTitle('Dark Temalar')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {DARK_THEMES.map((t) => (
            <ThemeCard key={t.id} theme={t} active={settings.theme === t.id} onSelect={handleThemeSelect} />
          ))}
        </div>

        {sectionTitle('Light Temalar')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {LIGHT_THEMES.map((t) => (
            <ThemeCard key={t.id} theme={t} active={settings.theme === t.id} onSelect={handleThemeSelect} />
          ))}
        </div>

        {sectionTitle('Editor Ayarları')}

        {row('Font Boyutu', (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="range" min={10} max={22} step={1}
              value={settings.fontSize}
              onChange={(e) => update('fontSize', Number(e.target.value))}
              style={{ width: 80, accentColor: '#388bfd' }}
            />
            <span style={{ color: '#e6edf3', fontSize: 11, minWidth: 22 }}>{settings.fontSize}</span>
          </div>
        ))}

        {row('Font Ailesi', (
          <select value={settings.fontFamily} onChange={(e) => update('fontFamily', e.target.value)} style={SELECT_STYLE}>
            {EDITOR_FONT_FAMILIES.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        ))}

        {row('Tab Boyutu', (
          <select value={settings.tabSize} onChange={(e) => update('tabSize', Number(e.target.value))} style={SELECT_STYLE}>
            {[2, 4, 8].map((n) => <option key={n} value={n}>{n} boşluk</option>)}
          </select>
        ))}

        {row('Satır Numaraları', (
          <select value={settings.lineNumbers} onChange={(e) => update('lineNumbers', e.target.value)} style={SELECT_STYLE}>
            <option value="on">Açık</option>
            <option value="relative">Göreli</option>
            <option value="off">Kapalı</option>
          </select>
        ))}

        {row('Satır Kaydırma', (
          <select value={settings.wordWrap} onChange={(e) => update('wordWrap', e.target.value)} style={SELECT_STYLE}>
            <option value="on">Açık</option>
            <option value="off">Kapalı</option>
            <option value="wordWrapColumn">Sütun Sınırı</option>
          </select>
        ))}

        {row('Minimap', (
          <button onClick={() => update('minimap', !settings.minimap)} style={{
            background: settings.minimap ? '#1f6feb20' : 'transparent',
            border: `1px solid ${settings.minimap ? '#388bfd' : '#30363d'}`,
            color: settings.minimap ? '#79c0ff' : '#8b949e',
            borderRadius: 4, fontSize: 10, padding: '3px 8px', cursor: 'pointer', fontWeight: 600,
          }}>{settings.minimap ? 'Açık' : 'Kapalı'}</button>
        ))}

        {row('Font Ligatures', (
          <button onClick={() => update('fontLigatures', !settings.fontLigatures)} style={{
            background: settings.fontLigatures ? '#1f6feb20' : 'transparent',
            border: `1px solid ${settings.fontLigatures ? '#388bfd' : '#30363d'}`,
            color: settings.fontLigatures ? '#79c0ff' : '#8b949e',
            borderRadius: 4, fontSize: 10, padding: '3px 8px', cursor: 'pointer', fontWeight: 600,
          }}>{settings.fontLigatures ? 'Açık' : 'Kapalı'}</button>
        ))}

        <button
          onClick={() => { onChange(EDITOR_SETTINGS_DEFAULTS); saveEditorSettings(EDITOR_SETTINGS_DEFAULTS); }}
          style={{
            marginTop: 10, width: '100%', padding: '6px 0',
            background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
            borderRadius: 4, fontSize: 11, cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#484f58'; e.currentTarget.style.color = '#e6edf3'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.color = '#8b949e'; }}
        >Varsayılana Sıfırla</button>
      </div>
    </div>
  );
});

/* ── Main Panel ── */
const FlowChartCodeEditorPanel = ({
  showCodeEditor,
  codePanelWidth,
  isResizing,
  setIsResizing,
  resizeStartXRef,
  resizeStartWidthRef,
  setShowCodeEditor,
  selectedShape,
  selectedShapeLabel,
  code,
  handleCodeChange,
  handleEditorDidMount,
  aiStatus,
  pendingChange,
  onApplyPendingChange,
  onDiscardPendingChange,
  isApplyingChange,
  currentPrompt,
  onPromptChange,
  onRunShapeAI,
  isRunningShapeAI,
  editorTabs,
  onTabActivate,
  onTabClose,
  onTabDetach,
}) => {
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const [promptPanelHeight, setPromptPanelHeight] = useState(30);
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editorSettings, setEditorSettings] = useState(() => loadEditorSettings());
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(0);
  const containerHeightRef = useRef(0);
  // ref'te anlık height: sürükleme sırasında setState yerine RAF+ref
  const promptHeightDraftRef = useRef(promptPanelHeight);
  const resizeRAFRef = useRef(null);
  const editorRef = useRef(null);

  const lang = useMemo(() => detectLang(selectedShapeLabel), [selectedShapeLabel]);
  const langStyle = useMemo(() => LANG_STYLE[lang] || LANG_STYLE.php, [lang]);
  const langLabel = useMemo(
    () => lang.toUpperCase().replace('JAVASCRIPT', 'JS').replace('TYPESCRIPT', 'TS'),
    [lang],
  );

  const currentFontFamily = useMemo(
    () => EDITOR_FONT_FAMILIES.find((f) => f.id === editorSettings.fontFamily)?.value || EDITOR_FONT_FAMILIES[0].value,
    [editorSettings.fontFamily],
  );

  // Büyük dokümanlarda Monaco scroll/render maliyeti ciddi artabiliyor.
  // Bu durumda bazı görsel özellikleri kapatıp kaydırmayı akıcı tutuyoruz.
  const isHeavyDocument = useMemo(() => {
    const text = typeof code === 'string' ? code : '';
    if (!text) return false;
    if (text.length > 18000) return true;
    let lineCount = 1;
    for (let i = 0; i < text.length; i++) {
      if (text.charCodeAt(i) === 10) lineCount++;
      if (lineCount > 500) return true;
    }
    return false;
  }, [code]);

  // Monaco options — sadece bağımlılıklar değişince yeniden oluşur
  const editorOptions = useMemo(() => ({
    ...EDITOR_BASE_OPTIONS,
    fontSize: editorSettings.fontSize,
    fontFamily: currentFontFamily,
    fontLigatures: editorSettings.fontLigatures,
    minimap: { enabled: !isHeavyDocument && editorSettings.minimap, scale: 1, renderCharacters: false },
    wordWrap: editorSettings.wordWrap,
    lineNumbers: editorSettings.lineNumbers,
    tabSize: editorSettings.tabSize,
    readOnly: Boolean(pendingChange),
    // PERFORMANS: Heavy doc'ta CPU'yu rahatlatan ek kısıtlar.
    renderValidationDecorations: isHeavyDocument ? 'off' : 'on',
    occurrencesHighlight: isHeavyDocument ? 'off' : 'singleFile',
    selectionHighlight: !isHeavyDocument,
    folding: !isHeavyDocument,
    mouseWheelZoom: false,
    stopRenderingLineAfter: isHeavyDocument ? 200 : 5000,
    // Heavy doc modunda hover/suggest'i de iyice yavaşlat
    hover: { enabled: !isHeavyDocument, delay: 600 },
    quickSuggestionsDelay: isHeavyDocument ? 400 : 150,
    inlineSuggest: { enabled: false },
  }), [
    editorSettings.fontSize,
    currentFontFamily,
    editorSettings.fontLigatures,
    editorSettings.minimap,
    editorSettings.wordWrap,
    editorSettings.lineNumbers,
    editorSettings.tabSize,
    pendingChange,
    isHeavyDocument,
  ]);

  const handleBeforeMount = useCallback((monaco) => {
    registerEditorAssets(monaco);
  }, []);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    clearEditorMarkers(editor, monaco);
    editor.addCommand(
      monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      () => editor.getAction('editor.action.formatDocument')?.run(),
    );
    if (typeof handleEditorDidMount === 'function') {
      handleEditorDidMount(editor, monaco);
    }
  }, [handleEditorDidMount]);

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  // Prompt panel sürüklemesi — sürükleme boyunca yalnızca ref güncellenir,
  // CSS transform ile görsel geri bildirim verilir, mouseUp'ta tek setState.
  const startPromptResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = e.currentTarget.parentElement;
    const rect = panel.getBoundingClientRect();
    containerHeightRef.current = rect.height;
    resizeStartYRef.current = e.clientY;
    resizeStartHeightRef.current = promptPanelHeight;
    promptHeightDraftRef.current = promptPanelHeight;
    setIsResizingPrompt(true);

    const onMove = (ev) => {
      if (resizeRAFRef.current) cancelAnimationFrame(resizeRAFRef.current);
      resizeRAFRef.current = requestAnimationFrame(() => {
        const pct = ((resizeStartYRef.current - ev.clientY) / (containerHeightRef.current || 600)) * 100;
        const next = Math.min(60, Math.max(15, resizeStartHeightRef.current + pct));
        promptHeightDraftRef.current = next;
        // Live visual: CSS variable üzerinden boyut güncelle
        const promptEl = panel?.querySelector('[data-prompt-panel]');
        if (promptEl) promptEl.style.flex = `0 0 ${next}%`;
      });
    };

    const onUp = () => {
      if (resizeRAFRef.current) cancelAnimationFrame(resizeRAFRef.current);
      setPromptPanelHeight(promptHeightDraftRef.current);
      setIsResizingPrompt(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [promptPanelHeight]);

  if (!showCodeEditor) return null;

  const multiTab = Array.isArray(editorTabs) && editorTabs.length > 0;

  const tbBtn = (active = false, color = '#388bfd') => ({
    background: active ? `${color}20` : 'transparent',
    border: `1px solid ${active ? `${color}80` : '#21262d'}`,
    color: active ? color : '#8b949e',
    padding: '2px 7px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
    fontWeight: active ? 600 : 500,
    display: 'flex', alignItems: 'center', gap: 4,
    flexShrink: 0, transition: 'all 0.12s', whiteSpace: 'nowrap',
  });

  return (
    <>
      {/* ── Horizontal Resize Handle ── */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          resizeStartXRef.current = e.clientX;
          resizeStartWidthRef.current = codePanelWidth;
          setIsResizing(true);
        }}
        style={{
          width: 5, background: isResizing ? '#388bfd' : '#21262d',
          cursor: 'col-resize', flexShrink: 0,
          position: 'relative', zIndex: 10000, userSelect: 'none',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { if (!isResizing) e.currentTarget.style.background = '#388bfd'; }}
        onMouseLeave={(e) => { if (!isResizing) e.currentTarget.style.background = '#21262d'; }}
      />

      {/* ── Panel ── */}
      <div style={{
        flex: `0 0 ${codePanelWidth}%`,
        minWidth: 180,
        maxWidth: '80%',
        width: `${codePanelWidth}%`,
        background: '#0d1117',
        borderLeft: '1px solid #21262d',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        contain: 'layout style',
      }}>

        {showSettings && (
          <SettingsPanel
            settings={editorSettings}
            onChange={setEditorSettings}
            onClose={() => setShowSettings(false)}
          />
        )}

        {/* ══ Tab Bar ══ */}
        <div style={{
          background: '#010409',
          borderBottom: '1px solid #21262d',
          display: 'flex',
          alignItems: 'stretch',
          height: 34,
          minWidth: 0,
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {multiTab ? (
            <div style={{
              display: 'flex', flex: 1, overflowX: 'auto', alignItems: 'stretch',
              scrollbarWidth: 'thin', scrollbarColor: '#30363d #010409', minWidth: 0,
            }}>
              {editorTabs.map((tab) => {
                const isActive = tab.shapeId === selectedShape;
                const tLang = detectLang(tab.shapeLabel);
                const tStyle = LANG_STYLE[tLang] || LANG_STYLE.php;
                const label = tab.shapeLabel || tab.shapeId || '';
                const shortLabel = label.includes('.') ? label.split('/').pop() : label;
                return (
                  <div
                    key={tab.shapeId}
                    onClick={() => onTabActivate?.(tab.shapeId)}
                    title={label}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '0 5px 0 11px',
                      cursor: 'pointer', flexShrink: 0,
                      borderRight: '1px solid #21262d',
                      borderBottom: isActive ? `2px solid ${tStyle.color}` : '2px solid transparent',
                      background: isActive ? '#0d1117' : 'transparent',
                      width: 140, minWidth: 70,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#161b22'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: tStyle.color, flexShrink: 0 }} />
                    <span style={{
                      color: isActive ? '#e6edf3' : '#7d8590',
                      fontSize: 11, fontWeight: isActive ? 500 : 400,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: 1, minWidth: 0,
                    }}>{shortLabel}</span>
                    <button onClick={(e) => { e.stopPropagation(); onTabDetach?.(tab.shapeId, tab.shapeLabel); }}
                      title="Yüzer panele çıkar"
                      style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', padding: '2px', fontSize: 10, flexShrink: 0, opacity: isActive ? 0.4 : 0, transition: 'opacity 0.15s, color 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#79c0ff'; e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; e.currentTarget.style.opacity = isActive ? '0.4' : '0'; }}
                    >↗</button>
                    <button onClick={(e) => { e.stopPropagation(); onTabClose?.(tab.shapeId); }}
                      title="Kapat"
                      style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', padding: '2px 3px', fontSize: 13, flexShrink: 0 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#e6edf3'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7, flex: 1, minWidth: 0,
              padding: '0 14px',
              borderBottom: `2px solid ${langStyle.color}`,
              background: '#0d1117', cursor: 'default', overflow: 'hidden',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <rect width="24" height="24" rx="4" fill={langStyle.bg} />
                <text x="12" y="17" textAnchor="middle" fontSize="10" fill={langStyle.color} fontWeight="700">
                  {langLabel.slice(0, 2)}
                </text>
              </svg>
              <span style={{
                color: '#cdd9e5', fontSize: 12, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1, minWidth: 0,
              }}>
                {selectedShape ? (selectedShapeLabel || selectedShape) : 'Script Editor'}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                color: langStyle.color, background: langStyle.bg,
                border: `1px solid ${langStyle.border}`, borderRadius: 4,
                padding: '2px 6px', flexShrink: 0,
              }}>{langLabel}</span>
            </div>
          )}
        </div>

        {/* ══ Toolbar ══ */}
        <div style={{
          background: '#010409',
          borderBottom: '1px solid #21262d',
          display: 'flex', alignItems: 'center',
          padding: '0 6px', height: 30,
          gap: 4, minWidth: 0, overflow: 'hidden', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {pendingChange ? (
              <>
                <button onClick={onDiscardPendingChange} disabled={isApplyingChange}
                  style={tbBtn(false, '#8b949e')}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#484f58'; e.currentTarget.style.color = '#e6edf3'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; }}
                >Undo</button>
                <button onClick={onApplyPendingChange} disabled={isApplyingChange}
                  style={{ ...tbBtn(true, '#238636'), background: isApplyingChange ? '#161b22' : 'linear-gradient(135deg,#1a7f37,#238636)' }}
                >Keep</button>
              </>
            ) : (
              selectedShape && (
                <button onClick={handleFormat} title="Kodu Formatla (Shift+Alt+F)"
                  style={tbBtn(false)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#388bfd'; e.currentTarget.style.color = '#79c0ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; }}
                >
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="2" y1="4" x2="14" y2="4" />
                    <line x1="2" y1="8" x2="10" y2="8" />
                    <line x1="2" y1="12" x2="12" y2="12" />
                    <polyline points="12 6 14 8 12 10" />
                  </svg>
                  Format
                </button>
              )
            )}
            <button onClick={() => setShowPromptPanel(!showPromptPanel)}
              style={tbBtn(showPromptPanel, '#6d28d9')}
              title="AI Agent Paneli"
              onMouseEnter={(e) => { if (!showPromptPanel) { e.currentTarget.style.borderColor = '#6d28d980'; e.currentTarget.style.color = '#a78bfa'; } }}
              onMouseLeave={(e) => { if (!showPromptPanel) { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; } }}
            >
              <span style={{ fontSize: 10 }}>✦</span>
              Agent
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            <button onClick={() => setShowSettings(true)}
              title="Editor Ayarları"
              style={tbBtn(false)}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#388bfd'; e.currentTarget.style.color = '#79c0ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
            <button onClick={() => setShowCodeEditor(false)}
              title="Kapat"
              style={{
                background: 'transparent', border: 'none', color: '#484f58',
                padding: '3px 5px', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                borderRadius: 4, transition: 'color 0.1s', flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#e6edf3'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
            >×</button>
          </div>
        </div>

        {/* ── Monaco Editor ── */}
        <div style={{
          flex: showPromptPanel ? `1 1 ${100 - promptPanelHeight}%` : '1',
          minHeight: 80, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          {selectedShape ? (
            /* PERFORMANS: `key` kaldırıldı — her shape değişiminde Monaco remount
               oluyordu (~400-800ms + worker yeniden yüklenmesi). Artık tek instance,
               shape değiştiğinde parent setValue ile içerik güncelliyor. */
            <Editor
              height="100%" width="100%"
              language={lang}
              value={code}
              theme={editorSettings.theme}
              onChange={handleCodeChange}
              onMount={handleEditorMount}
              beforeMount={handleBeforeMount}
              options={editorOptions}
            />
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', gap: 12, color: '#484f58',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Bir şekil seçin</span>
            </div>
          )}
        </div>

        {/* ── Vertical Resize (prompt panel) ── */}
        {showPromptPanel && (
          <div onMouseDown={startPromptResize} style={{
            height: 5, background: isResizingPrompt ? '#388bfd' : '#21262d',
            cursor: 'row-resize', flexShrink: 0, transition: 'background 0.15s',
          }}
            onMouseEnter={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#388bfd'; }}
            onMouseLeave={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#21262d'; }}
          />
        )}

        {/* ── AI Prompt Panel ── */}
        {showPromptPanel && (
          <div
            data-prompt-panel="true"
            style={{
              flex: `0 0 ${promptPanelHeight}%`, minHeight: 100, maxHeight: '60%',
              display: 'flex', flexDirection: 'column',
              background: '#0d1117', borderTop: '1px solid #21262d',
            }}>
            <div style={{
              background: '#010409', padding: '0 10px',
              borderBottom: '1px solid #21262d',
              display: 'flex', alignItems: 'center', gap: 8,
              height: 32, flexShrink: 0, minWidth: 0, overflow: 'hidden',
            }}>
              <span style={{ fontSize: 12, color: '#a78bfa', flexShrink: 0 }}>✦</span>
              <span style={{ color: '#cdd9e5', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>AI Prompt</span>
              <div style={{ flex: 1, minWidth: 0 }} />
              <button
                onClick={() => onRunShapeAI?.(currentPrompt)}
                disabled={!currentPrompt?.trim() || isRunningShapeAI}
                title="Prompt'u AI'a gönder ve kod üret"
                style={{
                  background: (!currentPrompt?.trim() || isRunningShapeAI)
                    ? 'transparent'
                    : 'linear-gradient(135deg,#3730a3,#6d28d9)',
                  border: `1px solid ${(!currentPrompt?.trim() || isRunningShapeAI) ? '#30363d' : '#7c3aed80'}`,
                  color: (!currentPrompt?.trim() || isRunningShapeAI) ? '#484f58' : '#e2d9f3',
                  padding: '3px 10px', borderRadius: 4, fontSize: 11,
                  cursor: (!currentPrompt?.trim() || isRunningShapeAI) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600,
                  transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                {isRunningShapeAI ? (
                  <>
                    <div style={{ width: 10, height: 10, border: '1.5px solid #484f58', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span>Üretiliyor…</span>
                  </>
                ) : (
                  <>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M4 4l16 8-16 8V4z" strokeLinejoin="round" />
                    </svg>
                    <span>Derle</span>
                  </>
                )}
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '10px 12px' }}>
              <textarea
                value={currentPrompt || ''}
                onChange={(e) => onPromptChange(e.target.value)}
                data-ignore-global-hotkeys="true"
                placeholder="AI talimatınızı buraya yazın..."
                style={{
                  width: '100%', height: '100%', background: '#161b22',
                  border: '1px solid #21262d', borderRadius: 6, color: '#e6edf3',
                  fontSize: 12.5,
                  fontFamily: "'Cascadia Code','Fira Code','JetBrains Mono',Consolas,'Courier New',monospace",
                  lineHeight: 1.6, padding: '10px 12px', resize: 'none',
                  outline: 'none', boxSizing: 'border-box', caretColor: '#79c0ff',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#388bfd'; }}
                onBlur={(e) => { e.target.style.borderColor = '#21262d'; }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(FlowChartCodeEditorPanel);
